import { AbstractCollection } from '../../abstract/Collection';
import { AbstractDocument, Timestampable, Untrackable } from '../../abstract/Document';
import type { Supplier } from '../Suppliers';
import { AppsInstagram } from './Apps/instagram';
import type { IEncryptionEnvelope, ISupplierAppsInstagram } from './Apps/instagram.types';
import { AppsMobiscrollConnect } from './Apps/mobiscrollConnect';
import type { WriteBatch } from 'firebase/firestore';
import type { ISupplierAppsMobiscrollConnect } from './Apps/mobiscrollConnect.types';

type Collections = ISupplierAppsInstagram | ISupplierAppsMobiscrollConnect;

// String literal type (add | for more apps)
export type ISupplierAppIds = 'instagram' | 'mobiscrollConnect';

@Timestampable
@Untrackable
export class App extends AbstractDocument<Collections> {
  readonly collections = {};
}

export class Apps extends AbstractCollection<App, Collections> {
  static path = 'apps';

  constructor(document: Supplier) {
    super(document.collection(Apps.path), App);
  }

  /**
   * Returns IDs of all connected apps existing in the collection.
   */
  async getConnectedApps() {
    const data = await this.query().get(true);
    const connectedApps: ISupplierAppIds[] = [];

    // Instagram is connected if instagram document exists and has an accessToken
    if (data.instagram && Apps.hasAccessToken(data.instagram)) {
      connectedApps.push('instagram');
    }

    if (data.mobiscrollConnect && Apps.hasAccessToken(data.mobiscrollConnect)) {
      connectedApps.push('mobiscrollConnect');
    }

    return connectedApps;
  }

  /** True when an app doc has a stored encrypted access token. */
  static hasAccessToken(data: { accessToken?: IEncryptionEnvelope } | undefined): boolean {
    return Boolean(data?.accessToken);
  }

  get instagram() {
    return this.getById('instagram');
  }

  get mobiscrollConnect() {
    return this.getById('mobiscrollConnect');
  }

  getById(key: 'instagram'): AppsInstagram & AppsInstagram['collections'];
  getById(key: 'mobiscrollConnect'): AppsMobiscrollConnect & AppsMobiscrollConnect['collections'];
  getById(key: 'instagram' | 'mobiscrollConnect', batch?: WriteBatch) {
    let document;

    switch (key) {
      case 'instagram':
        document = new (AbstractCollection as any)(this.reference, AppsInstagram);
        break;

      case 'mobiscrollConnect':
        document = new (AbstractCollection as any)(this.reference, AppsMobiscrollConnect);
        break;

      default:
        throw new Error('Unknown document ID.');
    }

    return document.getById(key, batch);
  }
}
